// app.js — entry point.
//
// Defines every custom element, hydrates the two language axes + loads the L1
// i18n pack, then fires `kfe:ready` so <l2b-app-shell> registers its routes and
// mounts the first screen. This is the file index.html loads as a module.
//
// Boot order (Brief 004): define elements → await initLanguages() (so the first
// paint has real strings, never raw keys) → first-run routing decision →
// dispatch `kfe:ready`. The KFE Web boot in src/app.js is the template.

import { defineComponent } from "./core/component.js";
import { t } from "./core/i18n.js";
import { trackOnce } from "./core/analytics.js";
import * as lang from "./i18n/language-manager.js";

import { L2BAppShell } from "./screens/l2b-app-shell.js";
import { L2BLanguageOnboarding } from "./screens/language-onboarding.js";
import { L2BDemoDashboard } from "./screens/demo-dashboard.js";
import { L2BExercise } from "./screens/l2b-exercise.js";
import { L2BTestExercise } from "./screens/l2b-test-exercise.js";
import { KFEL2BDragArea } from "./ui/kfe-l2b-drag-area.js";

// Brief 017 — boot telemetry for the inline catcher in index.html. The iOS
// third-party-iframe failure paints a blank blue field with no console to read,
// so the page has to report on itself: `phase()` leaves a breadcrumb of how far
// boot got, and the catcher renders it (or the thrown error) on screen. Every
// call is guarded — diagnostics must never be the thing that breaks boot.
/** @param {string} name */
function phase(name) {
  try { /** @type {any} */ (window).__l2bBootPhase = name; } catch { /* ignore */ }
}

/** Boot finished: tell the catcher to stand down. */
function signalBooted() {
  try {
    const w = /** @type {any} */ (window);
    w.__l2bBooted = true;
    if (typeof w.__l2bBootOk === "function") w.__l2bBootOk();
    document.dispatchEvent(new Event("l2b:booted"));
  } catch { /* ignore */ }
}

/** Boot threw: hand the real exception to the catcher's panel. @param {unknown} err */
function signalBootFailed(err) {
  try {
    const w = /** @type {any} */ (window);
    w.__l2bBootError = err;
    if (typeof w.__l2bBootFail === "function") w.__l2bBootFail(err);
  } catch { /* ignore */ }
}

// Order doesn't matter; tags resolve lazily.
defineComponent("l2b-app-shell", L2BAppShell);
defineComponent("l2b-language-onboarding", L2BLanguageOnboarding);
defineComponent("l2b-demo-dashboard", L2BDemoDashboard);
defineComponent("l2b-exercise", L2BExercise);
defineComponent("l2b-test-exercise", L2BTestExercise);
defineComponent("kfe-l2b-drag-area", KFEL2BDragArea);

// Synchronous and first (Brief 017): the shell element upgrades and can paint
// even if a later, awaitable boot step fails.
phase("components-defined");

// Keep the document <title> in sync with the active L1 (Success criterion 4).
document.addEventListener("kfe:i18n", () => { document.title = t("app.title"); });

// Defining <l2b-app-shell> above upgrades the element already in index.html,
// running its onConnected synchronously — so its `kfe:ready` / `kfe:i18n`
// listeners are attached by the time we dispatch below.
(async () => {
  try {
    // Hydrate both language axes from localStorage (or registry defaults) and
    // load the L1 i18n pack BEFORE first paint, so screens never flash raw keys.
    phase("init-languages");
    await lang.initLanguages();
    document.title = t("app.title");

    // Brief 011 (single-lesson embed): no onboarding, no dashboard. With no stored
    // choice, seed the default direction — an English speaker LEARNING MANDARIN
    // (target = zh, L1 = en): the stimulus audio is Mandarin, the ⓘ reveals
    // English. The "Learn English" header toggle flips it. Seeding via the setters
    // (not just localStorage) so the in-memory axes are right even when storage is
    // unavailable (private mode), and re-applies the L1 ("en") i18n pack. Returning
    // users keep their stored choice.
    phase("seed-languages");
    if (!lang.hasStoredChoices()) {
      lang.setTarget("zh");
      await lang.setMotherTongue("en");
    }

    // Direct open (Success criterion 1): only the Lesson 1 exercise exists on this
    // branch. Any other entry path ("/", "/dashboard", stray deep links) hard-
    // redirects to Lesson 1 so the app always boots straight into it. replaceState
    // so the redirect doesn't add a history entry.
    phase("route");
    const LESSON1 = "/exercise/103461";
    const path = location.pathname;
    if (!path.startsWith("/exercise/") && !path.startsWith("/test/")) {
      history.replaceState({}, "", LESSON1);
    }

    // The shell listens for kfe:ready to register routes + mount the first screen.
    phase("kfe-ready");
    document.dispatchEvent(new Event("kfe:ready"));

    // Brief 017: boot reached the end — the inline catcher stands down and any
    // panel it may already have shown is hidden again. Before the analytics call
    // below, which is best-effort and must never decide whether we "booted".
    phase("booted");
    signalBooted();

    // Brief 016: "someone entered the demo" — fired AFTER kfe:ready so the axes
    // are hydrated (the event carries target/L1) and so a blocked or throwing
    // gtag can never come between boot and the first mount. trackOnce, not track:
    // once per page load, never per render.
    trackOnce("demo_open");
  } catch (err) {
    // Brief 017: never leave a silent blue void. The inline catcher owns the
    // panel; hand it the real exception rather than waiting out its timeout.
    console.error("[l2b] boot failed", err);
    signalBootFailed(err);
  }
})();
